import { createWorker, type Worker } from "tesseract.js";
import {
  classifyLicenceSideFromPlainText,
  extractLicenceBackFieldsFromPlainText,
  extractLicenceFieldsFromPlainText,
  scoreLicenceBackOcrText,
  scoreLicenceFrontOcrText,
  type LicenceSideClassification,
} from "./licenceTextExtract";

let workerPromise: Promise<Worker> | null = null;

async function getLicenceOcrWorker(): Promise<Worker> {
  if (!workerPromise) {
    workerPromise = (async () => {
      const worker = await createWorker("eng");
      await worker.setParameters({
        tessedit_pageseg_mode: "6",
        preserve_interword_spaces: "1",
      } as any);
      return worker;
    })();
  }
  return workerPromise;
}

async function recognizeWithPsm(image: string, psm: string): Promise<string> {
  const worker = await getLicenceOcrWorker();
  await worker.setParameters({ tessedit_pageseg_mode: psm } as any);
  const result = await worker.recognize(image);
  return String(result?.data?.text ?? "");
}

async function bestOcrText(imageDataUrl: string, side: "front" | "back"): Promise<string> {
  const modes = ["6", "11", "4"];
  let bestText = "";
  let bestScore = -1;
  for (const psm of modes) {
    try {
      const text = await recognizeWithPsm(imageDataUrl, psm);
      const score = side === "front" ? scoreLicenceFrontOcrText(text) : scoreLicenceBackOcrText(text);
      if (score > bestScore) {
        bestScore = score;
        bestText = text;
      }
    } catch {
      /* try next mode */
    }
  }
  return bestText;
}

async function ocrTextForSideClassification(imageDataUrl: string): Promise<string> {
  const modes = ["6", "11", "4"];
  let bestText = "";
  let bestScore = -1;
  for (const psm of modes) {
    try {
      const text = await recognizeWithPsm(imageDataUrl, psm);
      const classified = classifyLicenceSideFromPlainText(text);
      const score = Math.max(classified.frontScore, classified.backScore);
      if (score > bestScore) {
        bestScore = score;
        bestText = text;
      }
    } catch {
      /* try next mode */
    }
  }
  return bestText;
}

export async function classifyLicenceSideFromImage(
  imageDataUrl: string,
): Promise<LicenceSideClassification> {
  const text = await ocrTextForSideClassification(imageDataUrl);
  return classifyLicenceSideFromPlainText(text);
}

export async function clientExtractLicenceFrontFromImage(imageDataUrl: string): Promise<{
  licence_number: string | null;
  licence_issue_date: string | null;
  licence_expiry: string | null;
}> {
  const text = await bestOcrText(imageDataUrl, "front");
  return extractLicenceFieldsFromPlainText(text);
}

export async function clientExtractLicenceBackFromImage(imageDataUrl: string): Promise<{
  licence_language: string | null;
  licence_restrictions: string | null;
  licence_restrictions_detected: boolean;
}> {
  const text = await bestOcrText(imageDataUrl, "back");
  const parsed = extractLicenceBackFieldsFromPlainText(text);
  const detected = parsed.licence_restrictions === "Yes" || parsed.licence_restrictions === "No";
  return {
    ...parsed,
    licence_restrictions_detected: detected,
  };
}

export async function imageRefToDataUrl(ref: string): Promise<string> {
  const trimmed = String(ref || "").trim();
  if (!trimmed) throw new Error("empty image ref");
  if (trimmed.startsWith("data:")) return trimmed;
  const res = await fetch(trimmed);
  if (!res.ok) throw new Error(`Could not load image (${res.status})`);
  const blob = await res.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsDataURL(blob);
  });
}
